import { useTranslation } from 'react-i18next';
import { useRouter } from '@tanstack/react-router';
import { useRequiredParams } from 'app/router/params';
import { useMemo, useState } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';

import Pagination from 'shared/data-display/Pagination';
import Spinner from 'shared/feedback/Spinner';
import { Typography } from 'app/components/ui';
import { usePagination } from 'app/utils/hooks/usePagination';
import { useCoverage } from 'app/api/coverage-queries';
import { RegionRow, TableRow } from 'app/api/coverage-types';
import { CoverageOverviewHeader } from './CoverageOverviewHeader';
import { CoverageOverviewTable } from './CoverageOverviewTable';

import './CoverageOverview.scss';
import { useEditorDocument } from 'app/api/documents/useEditorData';

type RowFilter = 'all' | 'complete' | 'partial' | 'missing';

const PAGE_SIZE = 15;
const EMPTY_STATS = { total: 0, complete: 0, partial: 0, missing: 0, tablesTotal: 0, tablesComplete: 0 };

export const CoverageOverview = () => {
  const { t } = useTranslation('tag');

  const { documentId, sectionId } = useRequiredParams('documentId', 'sectionId');
  const router = useRouter();
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<RowFilter>('all');

  const { data: pDocument } = useEditorDocument();
  const documentSuperId = pDocument?.superId;
  const { data, isFetching } = useCoverage(documentSuperId, show);

  const regionRows = useMemo(() => data?.regions ?? [], [data?.regions]);
  const tableRows = useMemo(() => data?.tables ?? [], [data?.tables]);

  const stats = useMemo(() => {
    if (!data) return EMPTY_STATS;
    return {
      total: data.stats.totalRegions,
      complete: data.stats.complete,
      partial: data.stats.partial,
      missing: data.stats.missing,
      tablesTotal: data.stats.tablesTotal,
      tablesComplete: data.stats.tablesComplete,
    };
  }, [data]);

  const filteredRegions = useMemo(() => {
    return regionRows.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (search && !r.heading.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [regionRows, filter, search]);

  const filteredTables = useMemo(() => {
    return tableRows.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (search && !r.tableName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [tableRows, filter, search]);

  const regionsPagination = usePagination(filteredRegions, { pageSize: PAGE_SIZE });
  const tablesPagination = usePagination(filteredTables, { pageSize: PAGE_SIZE });

  const goToRow = (row: RegionRow | TableRow) => {
    setShow(false);

    if (row.sectionSuperId !== sectionId) {
      const target = router.buildLocation({
        to: '/documents/$documentId/sections/$sectionId',
        params: { documentId: String(documentId), sectionId: String(row.sectionSuperId) },
        search: { taggingMode: true, taggingSubMode: 'mark-coverage' },
        hash: row.pageId ?? undefined,
      });
      window.location.href = target.href;
    }
  };

  return (
    <>
      <button className="tag-toolbar__button" onClick={() => setShow(true)}>
        <i className="fa-regular fa-lg fa-square-dashed-circle-check" />
        <Typography variant="body-5" fontWeight="bold">
          {t('coverage.overview.label')}
        </Typography>
      </button>

      <BootstrapModal show={show} onHide={() => setShow(false)} size="xl" contentClassName="coverage-overview-modal">
        <BootstrapModal.Header className="coverage-overview-modal__header">
          <CoverageOverviewHeader stats={stats} search={search} setSearch={setSearch} filter={filter} setFilter={setFilter} onClose={() => setShow(false)} />
        </BootstrapModal.Header>

        <BootstrapModal.Body className="coverage-overview-modal__body">
          {isFetching && !data ? (
            <div className="coverage-overview-modal__loading">
              <Spinner />
              <Typography variant="body-3">{t('coverage.overview.loading')}</Typography>
            </div>
          ) : (
            <CoverageOverviewTable
              regions={regionsPagination.paginatedItems}
              tables={tablesPagination.paginatedItems}
              onSelect={goToRow}
              regionsEmpty={filteredRegions.length === 0}
              tablesEmpty={filteredTables.length === 0}
              allEmpty={regionRows.length === 0 && tableRows.length === 0}
              regionsPagination={
                filteredRegions.length > PAGE_SIZE ? (
                  <Pagination onPaginate={(p: number) => regionsPagination.setCurrentPage(p)} page={regionsPagination.page} pageSize={PAGE_SIZE} />
                ) : null
              }
              tablesPagination={
                filteredTables.length > PAGE_SIZE ? (
                  <Pagination onPaginate={(p: number) => tablesPagination.setCurrentPage(p)} page={tablesPagination.page} pageSize={PAGE_SIZE} />
                ) : null
              }
            />
          )}
        </BootstrapModal.Body>
      </BootstrapModal>
    </>
  );
};
